"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createToken } from "@/src/action";
import { handleErrors, post } from "@/src/api/apiHandler";
import { showToast } from "@/src/components/toast/showToast";
import { useMergeActiveSale } from "@/src/hooks/useMergeActiveSale";
import useUserStore, { UserType } from "@/src/store/useUser";
import { convertPersianToEnglish } from "@/src/tools";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import OtpTimer from "../OTPTimer";
import OtpInput from "./OtpInput";

interface LoginFormProps {
    onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose }) => {
    const { addUser } = useUserStore();
    const [otpSent, setOtpSent] = useState(false);
    const [mobile, setMobile] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [timerActive, setTimerActive] = useState(false);
    const mergeActiveSale = useMergeActiveSale();

    // Web OTP API: run once on mount
    useEffect(() => {
        const controller = new AbortController();
        if ("credentials" in navigator && "OTPCredential" in window) {
            navigator.credentials
                .get({ otp: { transport: ["sms"] }, signal: controller.signal } as CredentialRequestOptions & { otp: { transport: string[] } })
                .then((otp: any) => {
                    if (otp?.code) setCode(otp.code);
                })
                .catch(console.error);
        }
        return () => controller.abort();
    }, []);

    // Auto-submit when full code entered
    useEffect(() => {
        if (otpSent && code.length === 4 && !loading) {
            handleSubmit();
        }
    }, [code, otpSent, loading]);

    const requestOtp = useCallback(async () => {
        if (mobile.length !== 11) {
            showToast("شماره موبایل خود را به درستی وارد نمایید", "info");
            return;
        }
        setLoading(true);
        try {
            await post("/user-message", { user_mobile: mobile });
            setOtpSent(true);
            setTimerActive(true);
            showToast("کد تایید با موفقیت برای شما ارسال شد", "success");
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    }, [mobile]);

    const verifyOtp = useCallback(async () => {
        setLoading(true);
        try {
            const res = await post("/user-login", { user_mobile: mobile, user_otp: code });
            const d = res.data;
            const user: UserType = {
                id: d.user_id,
                firstName: d.user_first_name,
                lastName: d.user_last_name,
                mobile: d.user_mobile,
                avatar: d.user_avatar,
                token: d.token,
            };
            await createToken(d.token);
            addUser(user);
            await mergeActiveSale();
            onClose();
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    }, [addUser, code, mergeActiveSale, mobile, onClose]);

    const handleSubmit = useCallback((e?: React.FormEvent) => {
        e?.preventDefault();
        if (otpSent) verifyOtp();
        else requestOtp();
    }, [otpSent, requestOtp, verifyOtp]);

    const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = e.target.value.replace(/[^0-9۰-۹]/g, "");
        setMobile(convertPersianToEnglish(cleaned));
    };

    const handleResend = () => {
        if (!timerActive || loading) return;
        requestOtp();
    };

    return (
        <div className="p-4 pb-0">
            <form onSubmit={handleSubmit}>
                {!otpSent ? (
                    <div>
                        <label htmlFor="mobile" className="block mb-1">شماره موبایل:</label>
                        <Input
                            id="mobile"
                            name="mobile"
                            placeholder="09123456789"
                            maxLength={11}
                            value={mobile}
                            dir="ltr"
                            onChange={handleMobileChange}
                            className="text-left placeholder:text-right"
                            required
                        />
                    </div>
                ) : (
                    <div className="space-y-1">
                        <p className="text-center">{mobile}</p>
                        <p className="text-center text-sm">کد یکبار مصرف ارسال شد</p>
                        <OtpInput value={code} onChange={setCode} />
                        <div className="text-center text-sm mt-2">
                            {code === "" ? (
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={!timerActive || loading}
                                    className="underline text-blue-600 disabled:opacity-50"
                                >
                                    <OtpTimer
                                        callback={setTimerActive}
                                        minute={1}
                                        second={59}
                                        start={timerActive}
                                    />
                                </button>
                            ) : (
                                <span>کد وارد شده: {code}</span>
                            )}
                        </div>
                    </div>
                )}
                <Button
                    type="submit"
                    disabled={loading || (!otpSent && mobile.length !== 11)}
                    className="mt-4 w-full"
                >
                    {loading ? <Loader2 className="animate-spin h-4 w-4" /> : otpSent ? 'ورود' : 'دریافت کد'}
                </Button>
            </form>
        </div>
    );
};

export default LoginForm;
